import { FlightTakeoff } from '@mui/icons-material'
import { Box, CircularProgress, InputBase, MenuItem, Paper, Typography } from '@mui/material'
import Popper from '@mui/material/Popper'
import { useTranslations } from 'next-intl'
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react'

interface IFromToInput {
    action: 'from' | 'to'
    optionListItems?: Record<string, any>[]
    initialValue?: string
    queryOptionListItems?: (query: string, action: 'from' | 'to') => void
    loading?: boolean
    onOptionSelect?: (option: string, action: 'from' | 'to', city?: string) => void
    active: boolean
    onActivate: () => void
    onClose: () => void
    hasError?: boolean
    srpResultsLoading?: boolean
    isPopularAirports?: boolean
}

export const FromToInput: FC<IFromToInput> = props => {
    const tCommon = useTranslations('common')
    const inputRef = useRef(null)
    const inputBaseRef = useRef<HTMLInputElement>(null)
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const [value, setValue] = useState(props.initialValue || '')

    const handleFocus = () => {
        props.onActivate()
        // Select all text in the input when focused
        if (inputBaseRef.current) {
            inputBaseRef.current.select()
        }
    }

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setValue(e.target.value)
        if (timerRef.current) {
            clearTimeout(timerRef.current)
        }
        timerRef.current = setTimeout(() => {
            props?.queryOptionListItems?.(e.target.value.trim(), props.action)
        }, 300)
    }

    const handleOptionSelect = (option: Record<string, any>) => {
        const selectedValue = `${option.payload.airportCode} - ${option.payload.cityName}, ${option.payload.countryCode}`
        setValue(selectedValue)
        props.onOptionSelect?.(selectedValue, props.action, option.payload.cityName )
        props.onClose()
    }

    // Focus input when component becomes active from external trigger
    useEffect(() => {
        if (props.active && inputBaseRef.current && document.activeElement !== inputBaseRef.current) {
            inputBaseRef.current.focus()
        }
    }, [props.active])

    // Update value when initialValue changes
    useEffect(() => {
        setValue(props.initialValue || '')
    }, [props.initialValue])

    return (
        <Box sx={{ position: 'relative', minWidth: 150, maxWidth: 300 }} ref={inputRef}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1
                }}
            >
                <InputBase
                    placeholder={tCommon(props.action)}
                    sx={{
                        ml: 1,
                        px: 1,
                        '& .MuiInputBase-input': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 100,
                            color: props.hasError ? 'error.main' : 'inherit',
                            fontSize: 14
                        }
                    }}
                    value={value}
                    onChange={handleChange}
                    autoComplete="off"
                    onFocus={handleFocus}
                    onClick={handleFocus}
                    inputRef={inputBaseRef}
                    readOnly={props.srpResultsLoading}
                />
            </Box>

            <Popper
                anchorEl={inputRef.current}
                open={props.active && (!!props.optionListItems?.length || !!props.loading)}
                placement={'bottom-start'}
                disablePortal={false} // Allow portal to body for proper layering
                style={{
                    zIndex: 1401,
                    width: inputRef.current ? (inputRef.current as HTMLElement).offsetWidth : undefined
                }}
            >
                <Paper sx={{ minWidth: 450, overflowY: 'auto', width: '100%', mt: 1.5 }} elevation={3}>
                    {(() => {
                        if (props.loading)
                            return (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )

                        return (
                            <Box>
                                {props.isPopularAirports && (
                                    <Typography p={1} variant="body2" color="textSecondary" fontWeight={500}>
                                        {tCommon('popularAirports')}
                                    </Typography>
                                )}
                                {props.optionListItems?.map((option, index) => (
                                    <MenuItem
                                        sx={{
                                            borderBottom: '1px solid #e0e0e0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            px: 1.5,
                                            py: 1.2
                                        }}
                                        key={`${option.payload.airportCode}-${index}`}
                                        onClick={() => handleOptionSelect(option)}
                                    >
                                        {/* Plane Icon */}
                                        <Box
                                            sx={{
                                                minWidth: 36,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <FlightTakeoff sx={{ fontSize: 28, color: 'grey.400' }} />
                                        </Box>
                                        {/* Airport Info */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box
                                                fontWeight={600}
                                                sx={{
                                                    fontSize: 14,
                                                    lineHeight: 1.5,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {option.payload.cityName}, {option.payload.countryName}
                                            </Box>
                                            <Box color="grey.600" fontSize="12px">
                                                {option.payload.airportName}
                                            </Box>
                                        </Box>
                                        {/* Airport Code */}
                                        <Box
                                            sx={{
                                                background: '#f3f4f6',
                                                color: '#222',
                                                borderRadius: 1,
                                                fontWeight: 600,
                                                fontSize: '12px',
                                                px: 1.5,
                                                py: 0.5,
                                                ml: 2,
                                                width: 48,
                                                textAlign: 'center'
                                            }}
                                        >
                                            {option.payload.airportCode}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Box>
                        )
                    })()}
                </Paper>
            </Popper>
        </Box>
    )
}
